import React, { useState, useCallback, useRef, useEffect } from "react";
import { copyToClipboard } from "@/utils/clipboard";

interface CaptionDisplayProps {
  caption: string;
  title: string;
  icon: React.ReactNode;
  bgColor: string;
  textColor: string;
  borderColor: string;
  maxLines?: number;
  onRegenerate?: () => void;
  isRegenerating?: boolean;
  canRegenerate?: boolean;
  onEdit?: () => void;
  canEdit?: boolean;
}

export const CaptionDisplay: React.FC<CaptionDisplayProps> = ({
  caption,
  title,
  icon,
  bgColor,
  textColor,
  borderColor,
  maxLines = 3,
  onRegenerate,
  isRegenerating = false,
  canRegenerate = false,
  onEdit,
  canEdit = false,
}) => {
  const [copied, setCopied] = useState(false);
  const [isTextTruncated, setIsTextTruncated] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isContainerHovered, setIsContainerHovered] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  const handleCopy = useCallback(async () => {
    await copyToClipboard(caption);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [caption]);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const element = textRef.current;
        setIsTextTruncated(element.scrollHeight > element.clientHeight);
      }
    };

    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [caption]);

  return (
    <div
      className={`p-3 ${bgColor} rounded-lg border ${borderColor}`}
      onMouseEnter={() => setIsContainerHovered(true)}
      onMouseLeave={() => setIsContainerHovered(false)}
    >
      <div className={`${isTextTruncated ? 'relative' : ''}`}>
        <p
          ref={textRef}
          className={`text-sm ${textColor} leading-relaxed whitespace-pre-wrap ${isTextTruncated ? 'cursor-help' : ''}`}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: maxLines,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
          title={isTextTruncated ? caption : undefined}
          onMouseEnter={() => isTextTruncated && setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          {caption}
        </p>

        {/* Hover tooltip for full caption - only shows if text is truncated and hovering */}
        {isTextTruncated && isHovering && (
          <div className="absolute bottom-full left-0 mb-2 w-80 max-w-(--breakpoint-sm) bg-gray-900 text-white text-sm p-3 rounded-lg shadow-xl transition-all duration-200 pointer-events-none z-50">
            <div className="font-medium mb-1">Full Caption:</div>
            <div className="whitespace-pre-wrap wrap-break-word">
              {caption}
            </div>
            {/* Arrow pointing down */}
            <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900"></div>
          </div>
        )}
      </div>

      {/* Action buttons - always visible at bottom left */}
      <div className="flex items-center justify-start gap-1 mt-2">
        {/* Edit button - show if edit is available */}
        {canEdit && onEdit ? (
          <button
            onClick={onEdit}
            className={`p-1.5 rounded-sm hover:bg-black/5 transition-all duration-200 ${textColor} hover:${textColor.replace('text-', 'text-').replace('-600', '-700').replace('-700', '-800')}`}
            title="Edit caption"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>
        ) : null}

        {/* Regenerate button */}
        {canRegenerate && onRegenerate ? (
          <button
            onClick={onRegenerate}
            disabled={isRegenerating}
            className={`p-1.5 rounded-sm hover:bg-black/5 transition-all duration-200 ${textColor} hover:${textColor.replace('text-', 'text-').replace('-600', '-700').replace('-700', '-800')} disabled:opacity-50 disabled:cursor-not-allowed`}
            title="Generate new caption with AI"
          >
            {isRegenerating ? (
              <svg
                className="w-4 h-4 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            ) : (
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            )}
          </button>
        ) : null}

        {/* Copy button - always visible */}
        <button
          onClick={handleCopy}
          className={`p-1.5 rounded-sm hover:bg-black/5 transition-all duration-200 ${textColor} hover:${textColor.replace('text-', 'text-').replace('-600', '-700').replace('-700', '-800')}`}
          title="Copy caption"
        >
        {copied ? (
          <svg
            className="w-4 h-4"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        ) : (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
          )}
        </button>
      </div>
    </div>
  );
};